import { Loop, PublishedWithChanges } from '@mui/icons-material';
import type { ICompetenceLevel } from '@sharedInterfaces/ICompetence';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { ShareDialog } from '@src/App/Components/Buttons/ShareButton/ShareButton';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import { useQuestionEmployeeSkillDialog } from '@src/App/Components/Dialogs/QuestionEmployeeSkill/QuestionEmployeeSkill';
import EmployeeOverlay from '@src/App/Components/Overlays/EmployeeOverlay/EmployeeOverlay';
import type { AppState } from '@store/store';
import { Share2 } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import New_ActiveBadge from '../../ItemBadge/New_ActiveBadge';
import New_AvailabilityBadge from '../../ItemBadge/New_AvailabilityBadge';
import New_CountBadge from '../../ItemBadge/New_CountBadge';
import New_MatchBadge from '../../ItemBadge/New_MatchBadge';
import SegmentLevelBadge from '../../ItemBadge/SegmentLevelBadge';
import Item from '../Item/Item';
import type { IItemAction } from '../Item/ItemAction';

interface EmployeeItemProps {
    title: string;
    id?: number;
    level?: number;
    count?: number;
    match?: number;
    active?: { activeUntil?: Date };
    disableOverlay?: boolean;
    percent?: number;
    skillId?: number;
    onClick?: string | (() => void);
    competenceLevels?: ICompetenceLevel[];
}

/**
 * Renders an employee item.
 *
 * @param {EmployeeItemProps} title - The title of the employee item.
 * @param {EmployeeItemProps} id - The id of the employee item.
 * @param {EmployeeItemProps} level - The level of the employee item.
 * @param {EmployeeItemProps} count - The count of the employee item.
 * @param {EmployeeItemProps} disableOverlay - Indicates if the overlay is disabled.
 * @param {EmployeeItemProps} onClick - The click event handler for the employee item.
 * @param {EmployeeItemProps} percent - The percent of the employee item.
 * @param {EmployeeItemProps} match - The match of the employee item.
 *
 * @returns {JSX.Element} The rendered employee item.
 */
function EmployeeItem({
    title,
    id,
    level,
    count,
    disableOverlay,
    onClick,
    percent,
    match,
    competenceLevels,
    active,
    skillId,
}: EmployeeItemProps) {
    const navigate = useNavigate();
    const questionEmployeeSkillDialog = useQuestionEmployeeSkillDialog();
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employees;
    const langStringsSkill = getLanguageTexts(lang).skills;
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    const employee = useSelector((state: AppState) => state.employees.find((e) => e.id === id));

    const itsMe = useSelector((state: AppState) => state.employee.id) === id;
    const [updating, setUpdating] = React.useState('');
    const [overlayOpen, setOverlayOpen] = React.useState(false);

    const thisAllEmployee = allEmployees.find((e) => e.id === id);
    const availabilityObj = thisAllEmployee?.availability;
    const availabilityText =
        !availabilityObj || (availabilityObj.availability === 0 && availabilityObj.workDayCount === 0)
            ? 'N/A'
            : `${Math.round(availabilityObj.availability)}%`;
    let titleLevel = '';
    let realPercent = percent;
    let newTitle = title;
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    if (level !== undefined) {
        if (competenceLevels) {
            if (level === 0) {
                titleLevel = langStrings.inPlaning;
                realPercent = 0;
                newTitle = `${title} - ${langStrings.inPlaningShort}`;
            } else {
                const levelTitle = competenceLevels[level - 1]?.title;
                titleLevel = `${langStrings.oftenUsed.level}: ${levelTitle}`;
                realPercent = (level / competenceLevels.length) * 100;
                newTitle = `${title} - ${levelTitle}`;
            }
        } else {
            titleLevel = `${langStrings.oftenUsed.level}: ${levelDefinitions[level ? level - 1 : 0]?.title}`;
            realPercent = (level / levelDefinitions.length) * 100;
        }
    }
    if (match !== undefined) {
        realPercent = match;
    }
    let realActive: undefined | boolean;
    if (active) {
        realActive = true;
        if (active?.activeUntil?.getTime) {
            const now = new Date();
            const daysDifference = (active.activeUntil.getTime() - now.getTime()) / (24 * 60 * 60 * 1000); // Unterschied in Tagen
            if (daysDifference <= 0) {
                realActive = false;
                realPercent = 0;
            } else if (daysDifference >= 365) {
                realPercent = 75;
            } else {
                realPercent = 20 + 55 * (daysDifference / 365);
            }
        }
    }

    const laodingIcon = <Loop fontSize="small" />;
    const actions: IItemAction[] = [];

    if (id && skillId !== undefined && level) {
        if (!itsMe) {
            actions.push({
                id: 'toQuestion',
                icon: updating === 'toQuestion' ? laodingIcon : <PublishedWithChanges fontSize="small" />,
                text: langStringsSkill.questionSkillTitle,
                helper: langStringsSkill.questionSkillHelper,
                onClick: () => {
                    setUpdating('toQuestion');
                    questionEmployeeSkillDialog(id, skillId, level).finally(() => {
                        setUpdating('');
                    });
                },
            });
        }
        if (employee) {
            actions.push({
                id: 'share',
                icon: <Share2 size={16} />,
                text: langStrings.oftenUsed.share,
                helper: langStrings.oftenUsed.share,
                onClick: () => {
                    openDialog(<ShareDialog id={'ShareDialog'} entity={employee} resolve={() => {}} />);
                },
            });
        }
    }

    const handleSingleClick = () => {
        if (id && !disableOverlay) {
            setOverlayOpen(true);
        }
    };

    const handleOverlayClose = () => {
        setOverlayOpen(false);
    };

    const handleDoubleClick = () => {
        if (onClick) {
            if (typeof onClick === 'string') {
                navigate(onClick);
            } else {
                onClick();
            }
        }
    };

    return (
        <Item
            onClick={handleSingleClick}
            onDoubleClick={handleDoubleClick}
            percent={realPercent}
            level={level && level >= 3 ? level : undefined} // Use actual level for contrast
            // order={level ? level * -1 : undefined}
            actions={actions}
            leftContent=<New_AvailabilityBadge
                availability={availabilityObj?.availability || null}
                title={langStrings.oftenUsed.availabilityNext28Days}
                size="medium"
            />
            rightContent={
                <>
                    {level !== undefined && <SegmentLevelBadge level={level} title={titleLevel} />}
                    {count !== undefined && (
                        <New_CountBadge
                            count={count}
                            title={`${langStrings.count} ${langStrings.oftenUsed.employees || 'Mitarbeitenden'}`}
                            size="medium"
                        />
                    )}
                    {match !== undefined && (
                        <New_MatchBadge match={match} title={langStrings.oftenUsed.similarity} size="medium" />
                    )}
                    {realActive !== undefined && (
                        <New_ActiveBadge
                            isActive={realActive}
                            label={realActive ? langStrings.oftenUsed.active : langStrings.oftenUsed.inactive}
                            title={
                                langStrings.oftenUsed.active +
                                (active?.activeUntil?.toLocaleDateString
                                    ? `, ${langStrings.activeUntil}: ${active.activeUntil.toLocaleDateString()}`
                                    : '')
                            }
                            size="medium"
                        />
                    )}
                </>
            }
        >
            {id ? (
                <EmployeeOverlay
                    key={id}
                    disabled={false}
                    employeeId={id}
                    open={overlayOpen}
                    onClose={handleOverlayClose}
                >
                    {newTitle}
                </EmployeeOverlay>
            ) : (
                newTitle
            )}
        </Item>
    );
}
export default EmployeeItem;
